import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { ChevronUp, ChevronDown, ChevronRight, Pencil, Plus, SlidersHorizontal, Snowflake } from 'lucide-react'
import type { TransactionModel, SqlModel, ColumnDef, TreeNode, PreviewMode, DevicePreview } from './types'
import FeedViewEditor from './FeedViewEditor'
import { groupColumns, groupLabel } from './setupCatalog'
import { actionName } from './types'
import {
  calendarDefaultForInput, isRichTextColumn, hasTextarea, hasUrlWidget, hasImageUpload, hasDocumentUpload,
} from '../../../livepreview/utils'
import MacCalendarIcon from '../../../livepreview/runtime/widgets/MacCalendarIcon'

interface PreviewPanelProps {
  model: TransactionModel
  selectedNode: TreeNode | null
  onSelectNode: (node: TreeNode) => void
  onModelChange: (model: TransactionModel) => void
  /** The form drawn (the canvas shows one form tab at a time). */
  formIndex: number
  /** Form / Grid / Feed — the canvas toolbar owns the switch. */
  mode: PreviewMode
  /** Append a column to a group (the dashed "+ Add column" tiles). */
  onAddColumn?: (groupName?: string) => void
}

export const deviceWidths: Record<DevicePreview, string> = {
  computer: '100%',
  tablet: '768px',
  mobile: '375px',
}

function isHidden(col: ColumnDef): boolean {
  const h = col.hidden
  return h === true || h === 'true' || h === '1' || (h as unknown) === 1
}

function isProtected(col: ColumnDef): boolean {
  const p = col.protect
  return p === true || p === 'true' || p === '1' || (p as unknown) === 1
}

function isMandatory(col: ColumnDef): boolean {
  const m = col.mandatory
  return m === true || m === 'true' || m === '1' || (m as unknown) === 1
}

// Format column name as a readable label when heading is not available.
// We only swap underscores for spaces so the designer's authored case
// is preserved (e.g. "ID" stays "ID", not "Id"; "Item_code" stays
// "Item code", not "Item Code").
function formatColumnLabel(col: ColumnDef): string {
  if (col.heading) return col.heading
  return (col.name || '').replace(/_/g, ' ')
}

function cloneModel(m: TransactionModel): TransactionModel {
  return JSON.parse(JSON.stringify(m))
}

// Per-form layout (form / grid / feed), held on the form itself as
// `view_layout` so Save keeps it and Live Preview opens on the same view.
// Unset: the first form shows as a form, the rest as grids.
export function formModeOf(model: TransactionModel, fi: number): PreviewMode {
  const saved = model.sql_models?.[fi]?.sql_model?.view_layout
  if (saved) return saved
  return fi === 0 ? 'form' : 'grid'
}

// One form's body on the canvas — its groups of fields, its grid, or its Feed
// wells. The chrome around it (form tabs, view switch, buttons, menus) is the
// DesignCanvas's.
export default function PreviewPanel({ model, selectedNode, onSelectNode, onModelChange, formIndex, mode, onAddColumn }: PreviewPanelProps) {
  // Scroll to the column picked elsewhere (Structure, search). A form-layout
  // field has an anchor; in grid mode the heading does.
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const node = selectedNode
    if (!node || node.formIndex !== formIndex || node.type !== 'column' || node.columnIndex == null) return
    const container = scrollRef.current
    if (!container) return
    const target = container.querySelector(`#${CSS.escape(`txn-pv-col-${node.formIndex}-${node.columnIndex}`)}`)
    if (target) (target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [selectedNode, formIndex])

  const sm = model.sql_models?.[formIndex]?.sql_model
  if (!sm) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
        No forms defined
      </div>
    )
  }
  const formProps: FormViewProps = {
    model, form: sm, selectedNode, onSelectNode, formIndex, onModelChange, onAddColumn,
  }
  return (
    <div ref={scrollRef}>
      {mode === 'feed' ? <FeedPreview {...formProps} />
        : mode === 'form' ? <FormPreview {...formProps} />
          : <GridPreview {...formProps} />}
    </div>
  )
}

// ---- Form Layout Preview ----

interface FormViewProps {
  model: TransactionModel
  form: SqlModel
  selectedNode: TreeNode | null
  onSelectNode: (node: TreeNode) => void
  formIndex: number
  onModelChange: (model: TransactionModel) => void
  onAddColumn?: (groupName?: string) => void
}

function FormPreview({ model, form, selectedNode, onSelectNode, formIndex, onModelChange, onAddColumn }: FormViewProps) {
  // In the designer canvas we keep hidden columns rendered — with a
  // dashed-border / "HIDDEN" marker on the card — so the author still
  // sees the column exists. They become invisible to the end user only
  // at runtime (the live-preview/runtime form filters them out via
  // visibleColumns).
  const visibleColumns = useMemo(() => {
    return (form.columns || [])
      .map((c, i) => ({ col: c.column, index: i }))
  }, [form.columns])

  // Group columns by group_name. Group order follows the position of the first
  // column in the group: prefer that column's y value, falling back to the
  // original columns-array order so authoring order is preserved when y is
  // unset. Within a group, columns sort by y (then by original index).
  // Basic Information stays pinned first; Others stays pinned last.
  // One ordering rule, shared with the Structure panel (setupCatalog).
  const groups = useMemo(() => groupColumns(form), [form])

  const isColumnSelected = (ci: number) => {
    return selectedNode?.type === 'column' && selectedNode.formIndex === formIndex && selectedNode.columnIndex === ci
  }

  // Drag and drop state
  const draggedRef = useRef<{ index: number; groupName: string } | null>(null)

  const handleDragStart = useCallback((e: React.DragEvent, colIndex: number, groupName: string) => {
    draggedRef.current = { index: colIndex, groupName }
    e.dataTransfer.effectAllowed = 'move'
    ;(e.currentTarget as HTMLElement).style.opacity = '0.5'
  }, [])

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    ;(e.currentTarget as HTMLElement).style.opacity = '1'
    draggedRef.current = null
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])

  // Drop now carries an `insertBefore` flag computed from cursor edge so the
  // dropped field lands on the chosen side of the target instead of always
  // landing before it.
  const handleDrop = useCallback((e: React.DragEvent, targetIndex: number, targetGroupName: string, insertBefore: boolean) => {
    e.preventDefault()
    const dragged = draggedRef.current
    if (!dragged || dragged.index === targetIndex) return

    const m = cloneModel(model)
    const formModel = m.sql_models[formIndex].sql_model
    const draggedCol = formModel.columns[dragged.index]

    if (!draggedCol) return

    // Capture the target group's current anchor (min y) BEFORE any mutation
    // so we can re-normalize y values starting from the same anchor. Without
    // this, re-normalising to 100,200,300… shifts the whole group's
    // position relative to other groups (since group order is decided by
    // each group's first column's y), and the form's group order
    // visibly reshuffles after a same-group drag.
    const targetGroupCurrentIdxs = formModel.columns
      .map((c, i) => ({ c, i }))
      .filter(({ c }) => (c.column.group_name || 'Others') === targetGroupName)
      .map(({ i }) => i)
    const baseY = targetGroupCurrentIdxs.length > 0
      ? Math.min(...targetGroupCurrentIdxs.map(i => Number(formModel.columns[i].column.y) || 0))
      : 100

    if (dragged.groupName !== targetGroupName) {
      draggedCol.column.group_name = targetGroupName
    }

    // Build the new visual order for the target group (sorted by current y)
    // and reposition the dragged column relative to the target.
    const groupOrder = formModel.columns
      .map((c, i) => ({ c, i }))
      .filter(({ c }) => (c.column.group_name || 'Others') === targetGroupName)
      .sort((a, b) => (Number(a.c.column.y) || 0) - (Number(b.c.column.y) || 0))
      .map(({ i }) => i)

    // Remove dragged from its current spot, then insert before/after target.
    const fromPos = groupOrder.indexOf(dragged.index)
    if (fromPos >= 0) groupOrder.splice(fromPos, 1)
    let targetPos = groupOrder.indexOf(targetIndex)
    if (targetPos === -1) targetPos = groupOrder.length
    const insertAt = insertBefore ? targetPos : targetPos + 1
    groupOrder.splice(insertAt, 0, dragged.index)

    // Re-normalize y values from the captured anchor so this group's first y
    // (and therefore its position relative to other groups) is preserved.
    const safeBase = Number.isFinite(baseY) && baseY > 0 ? baseY : 100
    groupOrder.forEach((columnIdx, visualOrder) => {
      formModel.columns[columnIdx].column.y = String(safeBase + visualOrder * 100)
    })

    onModelChange(m)
    draggedRef.current = null
  }, [model, formIndex, onModelChange])

  // Drop on a group's whitespace (not on a specific field). Within the same
  // group this is a no-op so a drag that lands between fields doesn't
  // teleport the column to the bottom — the user clearly didn't intend a
  // big reposition. Cross-group still appends to the end of the destination.
  const handleGroupDrop = useCallback((e: React.DragEvent, groupName: string) => {
    e.preventDefault()
    const dragged = draggedRef.current
    if (!dragged) return
    if (dragged.groupName === groupName) {
      draggedRef.current = null
      return
    }

    const m = cloneModel(model)
    const formModel = m.sql_models[formIndex].sql_model
    const draggedCol = formModel.columns[dragged.index]
    if (!draggedCol) return

    draggedCol.column.group_name = groupName

    // Append at the end of the destination group. Anchor (min y) of the
    // destination is preserved because we only extend max y, never min.
    const groupCols = formModel.columns
      .filter(c => (c.column.group_name || 'Others') === groupName)
    const maxY = Math.max(0, ...groupCols.map(c => Number(c.column.y) || 0))
    draggedCol.column.y = String(maxY + 100)

    onModelChange(m)
    draggedRef.current = null
  }, [model, formIndex, onModelChange])

  // Move a whole group up/down in the displayed order. Renumbers every
  // visible group's column y values using a 1000-step base + 100-step
  // intra-group spacing so groups stay well-separated and the old
  // y-overlap that disturbed grouping after drags is impossible.
  // 'Basic Information' is pinned first and 'Others' is pinned last —
  // the up/down controls don't cross those boundaries.
  const handleMoveGroup = useCallback((groupName: string, direction: 'up' | 'down') => {
    const m = cloneModel(model)
    const formModel = m.sql_models[formIndex].sql_model

    type Entry = { c: { column: ColumnDef }; i: number }
    const groupMap = new Map<string, Entry[]>()
    formModel.columns.forEach((c, i) => {
      const g = c.column.group_name || 'Others'
      if (!groupMap.has(g)) groupMap.set(g, [])
      groupMap.get(g)!.push({ c, i })
    })
    groupMap.forEach((cols) => cols.sort((a, b) => (Number(a.c.column.y) || 0) - (Number(b.c.column.y) || 0)))

    const entries: [string, Entry[]][] = [...groupMap.entries()]
    entries.sort((a, b) => {
      if (a[0] === 'Basic Information' || a[0] === 'Basic') return -1
      if (b[0] === 'Basic Information' || b[0] === 'Basic') return 1
      if (a[0] === 'Others' || a[0] === 'Other Information') return 1
      if (b[0] === 'Others' || b[0] === 'Other Information') return -1
      const ay = Number(a[1][0]?.c.column.y) || 0
      const by = Number(b[1][0]?.c.column.y) || 0
      return ay - by
    })

    const idx = entries.findIndex(([name]) => name === groupName)
    if (idx < 0) return
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1
    if (targetIdx < 0 || targetIdx >= entries.length) return

    // Don't cross Basic (always first) or Others (always last).
    const isBasic = (n: string) => n === 'Basic Information' || n === 'Basic'
    const isOthers = (n: string) => n === 'Others' || n === 'Other Information'
    if (isBasic(groupName) || isOthers(groupName)) return
    const swapName = entries[targetIdx][0]
    if (isBasic(swapName) || isOthers(swapName)) return

    const next = entries.slice()
    ;[next[idx], next[targetIdx]] = [next[targetIdx], next[idx]]

    next.forEach(([, cols], gi) => {
      const baseY = (gi + 1) * 1000
      cols.forEach((entry, j) => {
        formModel.columns[entry.i].column.y = String(baseY + j * 100)
      })
    })

    onModelChange(m)
  }, [model, formIndex, onModelChange])

  // Rename a group. Walks every column whose `group_name` matches `oldName`
  // (or '' / 'Others' when the special "Other Information" pseudonym is in
  // effect) and rewrites it to `newName`. No-op when the name is unchanged
  // or empty after trimming.
  const handleRenameGroup = useCallback((oldName: string, rawNewName: string) => {
    const newName = rawNewName.trim()
    if (!newName || newName === oldName) return
    const m = cloneModel(model)
    const formModel = m.sql_models[formIndex].sql_model
    formModel.columns.forEach((c) => {
      const cur = c.column.group_name || 'Others'
      if (cur === oldName) c.column.group_name = newName
    })
    // Its presentation settings follow the group to its new name.
    const gs = formModel.group_settings
    if (gs && gs[oldName] && !gs[newName]) {
      gs[newName] = gs[oldName]
      delete gs[oldName]
    }
    if (formModel.open_groups?.includes(oldName)) {
      formModel.open_groups = formModel.open_groups.map(g => (g === oldName ? newName : g))
    }
    // A detail form shown inside this header group moves with it.
    if (formIndex === 0) {
      m.sql_models.forEach((w) => {
        if (w.sql_model !== formModel && w.sql_model.display_in_group === oldName) {
          w.sql_model.display_in_group = newName
        }
      })
    }
    onModelChange(m)
  }, [model, formIndex, onModelChange])

  // Per-group presentation (group_settings, keyed by the name the columns
  // carry; '' for Others): starts open, and a display label.
  const groupKey = (g: string) => (g === 'Others' ? '' : g)
  const groupOpenByDefault = (g: string): boolean => {
    const st = form.group_settings?.[groupKey(g)]
    if (st && st.open !== undefined && st.open !== '') return st.open === true || st.open === 'true' || st.open === '1'
    const og = form.open_groups
    if (og && og.length) return og.includes(groupKey(g)) || og.includes(g)
    return groups[0]?.[0] === g
  }
  const patchGroupSetting = (g: string, patch: { label?: string; open?: boolean }) => {
    const m = cloneModel(model)
    const fm = m.sql_models[formIndex].sql_model
    const key = groupKey(g)
    const cur = { ...((fm.group_settings || {})[key] || {}) }
    if ('label' in patch) { if (patch.label) cur.label = patch.label; else delete cur.label }
    if ('open' in patch) cur.open = !!patch.open
    const next = { ...(fm.group_settings || {}), [key]: cur }
    if (!Object.keys(cur).length) delete next[key]
    fm.group_settings = Object.keys(next).length ? next : undefined
    onModelChange(m)
  }
  const [newGroup, setNewGroup] = useState<string | null>(null)

  return (
    <div>
      {groups.map(([groupName, cols], gi) => {
          const isBasic = groupName === 'Basic Information' || groupName === 'Basic'
          const isOthers = groupName === 'Others' || groupName === 'Other Information'
          // Up: not first row, not Basic itself, not against a pinned-Basic above.
          const aboveName = groups[gi - 1]?.[0]
          const aboveIsBasic = aboveName === 'Basic Information' || aboveName === 'Basic'
          // Down: not last row, not Others itself, not against a pinned-Others below.
          const belowName = groups[gi + 1]?.[0]
          const belowIsOthers = belowName === 'Others' || belowName === 'Other Information'
          const canMoveUp = !isBasic && !isOthers && gi > 0 && !aboveIsBasic
          const canMoveDown = !isBasic && !isOthers && gi < groups.length - 1 && !belowIsOthers
          // Rename is allowed for any group except the Others fallback (which
          // is the synthetic bucket for un-grouped columns — renaming it
          // wouldn't propagate to columns whose group_name is empty).
          const canRename = !isOthers
          return (
            <GroupSection
              key={groupName}
              groupName={groupName}
              label={groupLabel(form, groupName)}
              columns={cols}
              formIndex={formIndex}
              onSelectNode={onSelectNode}
              isColumnSelected={isColumnSelected}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onGroupDrop={handleGroupDrop}
              canMoveUp={canMoveUp}
              canMoveDown={canMoveDown}
              canRename={canRename}
              onMoveUp={() => handleMoveGroup(groupName, 'up')}
              onMoveDown={() => handleMoveGroup(groupName, 'down')}
              onRename={(next) => handleRenameGroup(groupName, next)}
              openByDefault={groupOpenByDefault(groupName)}
              onToggleOpenByDefault={(v) => patchGroupSetting(groupName, { open: v })}
              onLabel={(lbl) => patchGroupSetting(groupName, { label: lbl })}
              onAddColumn={onAddColumn ? () => onAddColumn(groupName) : undefined}
            />
          )
        })}

        {visibleColumns.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: '0.9625rem' }}>
            No columns yet.
          </div>
        )}

        {onAddColumn && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid var(--color-border)', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {newGroup === null ? (
              <>
                <button type="button" onClick={() => setNewGroup('')} style={dashedBtn}>
                  <Plus size={12} /> Add group
                </button>
                {visibleColumns.length === 0 && (
                  <button type="button" onClick={() => onAddColumn()} style={dashedBtn}>
                    <Plus size={12} /> Add column
                  </button>
                )}
              </>
            ) : (
              <>
                <input autoFocus value={newGroup} placeholder="Group name"
                  onChange={e => setNewGroup(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && newGroup.trim()) { onAddColumn(newGroup.trim()); setNewGroup(null) }
                    if (e.key === 'Escape') setNewGroup(null)
                  }}
                  style={{ padding: '5px 8px', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: '0.9375rem' }} />
                <button type="button" className="btn btn-secondary btn-sm" disabled={!newGroup.trim()}
                  onClick={() => { onAddColumn(newGroup.trim()); setNewGroup(null) }}>Add group with a column</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setNewGroup(null)}>Cancel</button>
              </>
            )}
          </div>
        )}
    </div>
  )
}

const dashedBtn: React.CSSProperties = {
  font: 'inherit', fontSize: '0.9375rem', color: 'var(--color-accent)', background: 'transparent',
  border: '1px dashed var(--color-border-strong, var(--color-text-faint))', borderRadius: 6, padding: '6px 12px',
  display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
}

interface GroupSectionProps {
  groupName: string
  /** Caption shown (group_settings label, else the name). */
  label: string
  columns: { col: ColumnDef; index: number }[]
  formIndex: number
  onSelectNode: (node: TreeNode) => void
  isColumnSelected: (ci: number) => boolean
  onDragStart: (e: React.DragEvent, colIndex: number, groupName: string) => void
  onDragEnd: (e: React.DragEvent) => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent, targetIndex: number, targetGroupName: string, insertBefore: boolean) => void
  onGroupDrop: (e: React.DragEvent, groupName: string) => void
  canMoveUp: boolean
  canMoveDown: boolean
  canRename: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  onRename: (newName: string) => void
  /** Starts expanded on the record form (group_settings.open). */
  openByDefault: boolean
  onToggleOpenByDefault: (open: boolean) => void
  /** Display label (group_settings.label); '' clears it. */
  onLabel: (label: string) => void
  onAddColumn?: () => void
}

function GroupSection({
  groupName, label, columns, formIndex, onSelectNode, isColumnSelected,
  onDragStart, onDragEnd, onDragOver, onDrop, onGroupDrop,
  canMoveUp, canMoveDown, canRename, onMoveUp, onMoveDown, onRename,
  openByDefault, onToggleOpenByDefault, onLabel, onAddColumn,
}: GroupSectionProps) {
  const [collapsed, setCollapsed] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draftName, setDraftName] = useState('')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const settingsRef = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!settingsOpen) return
    const close = (e: MouseEvent) => {
      if (settingsRef.current && e.target instanceof Node && settingsRef.current.contains(e.target)) return
      setSettingsOpen(false)
    }
    window.addEventListener('mousedown', close)
    return () => window.removeEventListener('mousedown', close)
  }, [settingsOpen])

  const beginEdit = () => {
    setDraftName(groupName === 'Others' ? 'Other Information' : groupName)
    setEditing(true)
  }
  const commitEdit = () => {
    if (editing) {
      onRename(draftName)
      setEditing(false)
    }
  }
  const cancelEdit = () => setEditing(false)

  const ctrlBtn = (lbl: string, disabled: boolean, onClick: () => void, icon: React.ReactNode): React.ReactNode => (
    <button
      type="button"
      title={lbl}
      aria-label={lbl}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onClick() }}
      style={{
        width: 26, height: 26, background: 'none', border: 'none', borderRadius: 6,
        cursor: disabled ? 'default' : 'pointer',
        color: 'var(--color-text-secondary)', opacity: disabled ? 0.35 : 1,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}
    >{icon}</button>
  )

  return (
    <section
      style={{
        padding: '14px 20px 18px',
        borderBottom: '1px solid var(--color-border)',
        background: dragOver ? 'var(--color-accent-weak)' : 'transparent',
      }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { setDragOver(false); onGroupDrop(e, groupName) }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: collapsed ? 0 : 12, flexWrap: 'wrap' }}>
        {ctrlBtn(collapsed ? 'Expand group' : 'Collapse group', false, () => setCollapsed(!collapsed),
          collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />)}
        {editing ? (
          <input
            autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitEdit() }
              else if (e.key === 'Escape') { e.preventDefault(); cancelEdit() }
            }}
            aria-label="Group name"
            style={{
              minWidth: 0, padding: '2px 6px', fontSize: '1rem', fontWeight: 600, color: 'var(--color-text)',
              background: 'var(--color-input-bg)', border: '1px solid var(--color-accent)', borderRadius: 4,
            }}
          />
        ) : (
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }} title={label !== groupName ? `Group ${groupName}` : undefined}>{label}</h3>
        )}
        {canRename && !editing && ctrlBtn('Rename group', false, beginEdit, <Pencil size={12} />)}
        <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
          {columns.length} field{columns.length === 1 ? '' : 's'}
          {' · '}{openByDefault ? 'open by default' : 'collapsed by default'}
        </span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          <span ref={settingsRef} style={{ position: 'relative' }}>
            {ctrlBtn('Group settings: label, open by default', false, () => setSettingsOpen(o => !o), <SlidersHorizontal size={13} />)}
            {settingsOpen && (
              <div role="dialog" aria-label="Group settings" style={{
                position: 'absolute', right: 0, top: '100%', zIndex: 20, width: 260, padding: 12,
                background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.16)', display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                  Label shown on the form
                  <input defaultValue={label === groupName || (groupName === 'Others' && label === 'Other Information') ? '' : label}
                    placeholder={groupName === 'Others' ? 'Other Information' : groupName}
                    onBlur={e => onLabel(e.target.value.trim())}
                    onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                    style={{ padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: '0.9375rem' }} />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9375rem' }}>
                  <input type="checkbox" checked={openByDefault} onChange={e => onToggleOpenByDefault(e.target.checked)} />
                  Open when the record opens
                </label>
              </div>
            )}
          </span>
          {ctrlBtn('Move group up', !canMoveUp, onMoveUp, <ChevronUp size={14} />)}
          {ctrlBtn('Move group down', !canMoveDown, onMoveDown, <ChevronDown size={14} />)}
        </span>
      </div>
      {!collapsed && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
          gap: '12px 20px',
        }}>
          {columns.map(({ col, index }) => (
            <FieldPreview
              key={`${col.name}-${index}`}
              col={col}
              colIndex={index}
              anchorId={`txn-pv-col-${formIndex}-${index}`}
              groupName={groupName}
              selected={isColumnSelected(index)}
              onClick={() => onSelectNode({ type: 'column', formIndex, columnIndex: index, label: col.name })}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDragOver={onDragOver}
              onDrop={onDrop}
            />
          ))}
          {onAddColumn && (
            <button type="button" onClick={onAddColumn} title={`Add a column to ${label}`} style={{
              font: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              minHeight: 58, border: '1.5px dashed var(--color-accent)', borderRadius: 8,
              background: 'var(--color-accent-weak)', color: 'var(--color-accent)', fontSize: '0.9375rem',
              fontWeight: 500, cursor: 'pointer',
            }}>
              <Plus size={14} /> Add column
            </button>
          )}
        </div>
      )}
    </section>
  )
}

interface FieldPreviewProps {
  col: ColumnDef
  colIndex: number
  /** DOM id used as a scroll anchor when this column is selected in the tree. */
  anchorId?: string
  groupName: string
  selected: boolean
  onClick: () => void
  onDragStart: (e: React.DragEvent, colIndex: number, groupName: string) => void
  onDragEnd: (e: React.DragEvent) => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent, targetIndex: number, targetGroupName: string, insertBefore: boolean) => void
}

function FieldPreview({ col, colIndex, anchorId, groupName, selected, onClick, onDragStart, onDragEnd, onDragOver, onDrop }: FieldPreviewProps) {
  // 'left' = insert before this field, 'right' = insert after, null = no indicator
  const [dropSide, setDropSide] = useState<'left' | 'right' | null>(null)

  const getInputType = () => {
    switch ((col.col_type || '').toUpperCase()) {
      case 'NUMBER': return 'number'
      case 'DATE': return 'date'
      case 'DATETIME': return 'datetime-local'
      case 'BOOLEAN': return 'checkbox'
      default: return 'text'
    }
  }
  const inputType = getInputType()
  const prot = isProtected(col)
  const mand = isMandatory(col)
  const hidden = isHidden(col)
  // Editable fields use the elevated input bg (white in light theme)
  // so designers can tell at a glance which columns the user can type
  // into. Protected fields keep the muted panel bg.
  const editableBg = 'var(--lp-input-bg)'
  const protectedBg = 'var(--color-bg-secondary)'

  const renderInput = () => {
    if (col.lookup || (col.edit_mask?.mask === 'ddlb')) {
      return (
        <select
          style={{
            width: '100%', padding: '4px 8px', fontSize: '0.9375rem',
            background: prot ? protectedBg : editableBg,
            border: '1px solid var(--color-border)',
            borderRadius: 4, color: 'var(--color-text)',
          }}
          disabled={prot}
        >
          <option value="">Select...</option>
          {Array.isArray(col.edit_mask?.values) && col.edit_mask.values.map((v, i) => (
            <option key={i} value={v.data}>{v.display}</option>
          ))}
        </select>
      )
    }

    if (col.edit_mask?.mask === 'bool_check' || col.edit_mask?.mask === 'checkbox' || inputType === 'checkbox') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 30 }}>
          <input type="checkbox" disabled={prot} />
        </div>
      )
    }

    if (col.edit_mask?.mask === 'radio') {
      return (
        <div style={{ display: 'flex', gap: 8 }}>
          {(col.edit_mask.values || [{ data: 'yes', display: 'Yes' }, { data: 'no', display: 'No' }]).map((v, i) => (
            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.9375rem', color: 'var(--color-text)' }}>
              <input type="radio" name={col.name} disabled={prot} />
              {v.display}
            </label>
          ))}
        </div>
      )
    }

    const baseInputStyle = {
      width: '100%', padding: '4px 8px', fontSize: '0.9375rem',
      background: prot ? protectedBg : editableBg,
      border: '1px solid var(--color-border)',
      borderRadius: 4, color: 'var(--color-text)', opacity: prot ? 0.6 : 1,
    } as const

    // ── Presentation widgets (mock, non-interactive) ──────────────────
    if (isRichTextColumn(col)) {
      return (
        <div style={{ ...baseInputStyle, padding: 0, overflow: 'hidden' }}>
          <div style={{
            display: 'flex', gap: 10, padding: '4px 8px', fontSize: '0.8125rem', fontWeight: 600,
            color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)',
          }}>
            <span>Paragraph ▾</span><b>B</b><i>I</i><u>U</u><span>• List</span><span>🔗</span><span>🖼</span><span>▦</span>
          </div>
          <div style={{ minHeight: 64, padding: '6px 8px', color: 'var(--color-text-secondary)' }}>
            {col.descr || 'Rich text — headings, lists, links, tables and images'}
          </div>
        </div>
      )
    }
    if (hasTextarea(col)) {
      return (
        <textarea
          placeholder={col.descr || col.heading}
          disabled={prot}
          rows={2}
          style={{ ...baseInputStyle, resize: 'none', minHeight: 48 }}
        />
      )
    }
    if (hasUrlWidget(col)) {
      return (
        <div style={{ display: 'flex', gap: 6 }}>
          <input type="text" placeholder="https://…" disabled={prot} style={{ ...baseInputStyle, flex: 1 }} />
          <span style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px',
            border: '1px solid var(--color-border)', borderRadius: 4,
            color: 'var(--color-text-secondary)', background: 'var(--color-bg-secondary)', fontSize: '0.8125rem',
          }}>↗</span>
        </div>
      )
    }
    if (hasImageUpload(col) || hasDocumentUpload(col)) {
      const isImg = hasImageUpload(col)
      return (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
          border: '1px dashed var(--color-border)', borderRadius: 6,
          background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)',
          fontSize: '0.8425rem',
        }}>
          {isImg ? '🖼' : '📄'} {isImg ? 'Add image' : 'Add document'}
        </div>
      )
    }
    if (inputType === 'date' || inputType === 'datetime-local') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MacCalendarIcon value={col.default_value} size={30} />
          <input type={inputType} placeholder={col.descr || col.heading} disabled={prot}
            style={{ ...baseInputStyle, flex: 1 }} />
        </div>
      )
    }

    // Date/datetime are handled above; here inputType is text/number/checkbox,
    // for which the default value is just the literal.
    const value = col.default_value || ''

    return (
      <input
        type={inputType}
        placeholder={col.descr || col.heading}
        disabled={prot}
        defaultValue={value}
        style={baseInputStyle}
      />
    )
  }

  return (
    <div
      id={anchorId}
      draggable
      onClick={onClick}
      onDragStart={(e) => onDragStart(e, colIndex, groupName)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        onDragOver(e)
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
        const side: 'left' | 'right' = (e.clientX - rect.left) < rect.width / 2 ? 'left' : 'right'
        setDropSide(prev => prev === side ? prev : side)
      }}
      onDragLeave={() => setDropSide(null)}
      onDrop={(e) => {
        e.stopPropagation()
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
        const insertBefore = (e.clientX - rect.left) < rect.width / 2
        setDropSide(null)
        onDrop(e, colIndex, groupName, insertBefore)
      }}
      style={{
        cursor: 'grab',
        padding: '4px 6px',
        borderRadius: 4,
        position: 'relative',
        border: selected
          ? '1px solid var(--color-accent)'
          : hidden
            ? '1px dashed var(--color-text-secondary)'
            : '1px solid transparent',
        background: selected
          ? 'rgba(76,141,255,0.05)'
          : hidden
            ? 'repeating-linear-gradient(45deg, transparent 0 6px, rgba(127,127,127,0.07) 6px 12px)'
            : 'transparent',
        opacity: hidden ? 0.7 : 1,
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      {/* Drop-position indicator: a 3px accent bar on the chosen edge so the
          author sees exactly where the dragged field will land. */}
      {dropSide && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -2, bottom: -2,
            left: dropSide === 'left' ? -4 : 'auto',
            right: dropSide === 'right' ? -4 : 'auto',
            width: 3,
            background: 'var(--color-accent)',
            borderRadius: 2,
            boxShadow: '0 0 0 2px rgba(76,141,255,0.25)',
            pointerEvents: 'none',
          }}
        />
      )}
      <label style={{
        display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
        marginBottom: 2, cursor: 'grab',
      }}>
        {formatColumnLabel(col)}
        {mand && <span style={{ color: 'var(--color-error, #cf222e)', marginLeft: 2 }}>*</span>}
        {col.key && <span style={{ color: 'var(--color-warning, #9a6700)', marginLeft: 4, fontSize: '0.8125rem' }}>PK</span>}
        {hidden && (
          <span
            title="Hidden at runtime — visible here in the designer only"
            style={{
              marginLeft: 6, padding: '1px 6px', borderRadius: 10,
              background: 'rgba(127,127,127,0.18)',
              color: 'var(--color-text-secondary)',
              fontSize: '0.8125rem', fontWeight: 700, letterSpacing: 0.4,
              textTransform: 'uppercase',
            }}
          >Hidden</span>
        )}
      </label>
      {renderInput()}
    </div>
  )
}

// ---- Feed view (how one record reads inside something else) ----
//
// Wrapped in the same FormHeader as the other two layouts so the switch reads
// as three views of ONE form, not as a separate screen.

function FeedPreview({
  model, form, formIndex, onModelChange,
}: FormViewProps) {
  return (
    <div style={{ padding: 16 }}>
      <FeedViewEditor
        model={model}
        form={form}
        formIndex={formIndex}
        onModelChange={onModelChange}
      />
    </div>
  )
}

// ---- Grid Layout Preview ----

const DEFAULT_COL_WIDTH = 160

function GridPreview({ model, form, selectedNode, onSelectNode, formIndex, onModelChange, onAddColumn }: FormViewProps) {
  // Sort columns by x value for grid layout. Hidden columns stay
  // rendered (with a dashed-border marker) so the designer can still
  // see, select, and reposition them.
  const visibleColumns = useMemo(() => {
    return (form.columns || [])
      .map((c, i) => ({ col: c.column, index: i }))
      .sort((a, b) => (Number(a.col.x) || 0) - (Number(b.col.x) || 0))
  }, [form.columns])

  const isColumnSelected = (ci: number) => {
    return selectedNode?.type === 'column' && selectedNode.formIndex === formIndex && selectedNode.columnIndex === ci
  }

  // Column drag with edge detection (insert-before vs insert-after / swap)
  const draggedRef = useRef<number | null>(null)
  const [dropIndicator, setDropIndicator] = useState<{ visIdx: number; side: 'left' | 'right' } | null>(null)
  // Tracks an active resize so a drag started inside the resize handle is
  // cancelled. Without this guard, mousedown on the handle + a 5px move
  // triggers the th's native dragstart, clobbering the resize.
  const resizingFlagRef = useRef(false)

  const handleHeaderDragStart = useCallback((e: React.DragEvent, origIndex: number) => {
    if (resizingFlagRef.current) {
      e.preventDefault()
      return
    }
    draggedRef.current = origIndex
    e.dataTransfer.effectAllowed = 'move'
    ;(e.currentTarget as HTMLElement).style.opacity = '0.5'
  }, [])

  const handleHeaderDragEnd = useCallback((e: React.DragEvent) => {
    ;(e.currentTarget as HTMLElement).style.opacity = '1'
    draggedRef.current = null
    setDropIndicator(null)
  }, [])

  const handleHeaderDragOver = useCallback((e: React.DragEvent, visIdx: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (draggedRef.current === null) return
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const offsetX = e.clientX - rect.left
    const side: 'left' | 'right' = offsetX < rect.width / 2 ? 'left' : 'right'
    setDropIndicator(prev =>
      prev && prev.visIdx === visIdx && prev.side === side ? prev : { visIdx, side }
    )
  }, [])

  const handleHeaderDrop = useCallback((e: React.DragEvent, targetVisIdx: number) => {
    e.preventDefault()
    const fromOrigIndex = draggedRef.current
    if (fromOrigIndex === null) {
      setDropIndicator(null)
      return
    }

    const targetEntry = visibleColumns[targetVisIdx]
    if (!targetEntry || fromOrigIndex === targetEntry.index) {
      draggedRef.current = null
      setDropIndicator(null)
      return
    }

    // Determine drop side from cursor position (final read; handles edge correctly even
    // if dragOver hasn't fired immediately before drop).
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const offsetX = e.clientX - rect.left
    const insertBefore = offsetX < rect.width / 2

    const m = cloneModel(model)
    const formModel = m.sql_models[formIndex].sql_model

    const sorted = [...visibleColumns]
    const fromVisIdx = sorted.findIndex(c => c.index === fromOrigIndex)
    if (fromVisIdx === -1) {
      draggedRef.current = null
      setDropIndicator(null)
      return
    }

    const [moved] = sorted.splice(fromVisIdx, 1)
    // Adjusted target visual index after removal
    const adjustedTargetVis = targetVisIdx > fromVisIdx ? targetVisIdx - 1 : targetVisIdx
    const insertAt = insertBefore ? adjustedTargetVis : adjustedTargetVis + 1
    sorted.splice(insertAt, 0, moved)

    // Update x values to reflect new visual order
    sorted.forEach((c, visualIndex) => {
      formModel.columns[c.index].column.x = String((visualIndex + 1) * 10)
    })

    onModelChange(m)
    draggedRef.current = null
    setDropIndicator(null)
  }, [visibleColumns, model, formIndex, onModelChange])

  // Column resize
  const tableRef = useRef<HTMLTableElement>(null)
  const resizingRef = useRef<{ thEl: HTMLElement; startX: number; startWidth: number; origIndex: number; visIdx: number } | null>(null)

  // Sum of column widths — required as an explicit table width so that
  // tableLayout:'fixed' is actually engaged. With width:'auto' browsers
  // fall back to auto layout, and `<col>` widths are then ignored when
  // they're narrower than the column's content (so resize "did nothing"
  // even though style.width was set). The table also takes its intrinsic
  // size from this value, which is what the original `width:'auto'`
  // intent was — show empty space on the right rather than stretching.
  const tableWidth = useMemo(() => {
    return visibleColumns.reduce((sum, { col }) => sum + (col.width || DEFAULT_COL_WIDTH), 0)
  }, [visibleColumns])

  const handleResizeStart = useCallback((e: React.MouseEvent, origIndex: number, visIdx: number) => {
    e.preventDefault()
    e.stopPropagation()
    const th = (e.currentTarget as HTMLElement).parentElement!
    resizingRef.current = { thEl: th, startX: e.pageX, startWidth: th.offsetWidth, origIndex, visIdx }
    resizingFlagRef.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    // Snapshot table width at drag start so we can adjust it by the same
    // delta as the column. Without this the table's fixed width keeps the
    // dragged column from actually changing size.
    const startTableWidth = tableRef.current?.offsetWidth ?? 0

    const handleMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return
      const delta = ev.pageX - resizingRef.current.startX
      const newWidth = Math.max(50, resizingRef.current.startWidth + delta)
      const widthDelta = newWidth - resizingRef.current.startWidth
      // Update both th and colgroup col for live preview
      resizingRef.current.thEl.style.width = `${newWidth}px`
      if (tableRef.current) {
        const col = tableRef.current.querySelector(`colgroup col:nth-child(${resizingRef.current.visIdx + 1})`) as HTMLElement
        if (col) col.style.width = `${newWidth}px`
        // Grow/shrink the table by the same delta so the freed (or required)
        // space comes from the table's right edge, not from neighbouring
        // columns. Table-layout:fixed will otherwise redistribute.
        tableRef.current.style.width = `${Math.max(50, startTableWidth + widthDelta)}px`
      }
    }

    const handleMouseUp = (ev: MouseEvent) => {
      if (!resizingRef.current) return
      const delta = ev.pageX - resizingRef.current.startX
      const newWidth = Math.max(50, resizingRef.current.startWidth + delta)

      // Skip the model write when nothing actually moved (a click on the
      // handle without drag) so undo history stays clean.
      if (Math.abs(delta) >= 1) {
        const m = cloneModel(model)
        m.sql_models[formIndex].sql_model.columns[resizingRef.current.origIndex].column.width = newWidth
        onModelChange(m)
      }

      resizingRef.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      // Release the drag-block flag a tick later so any in-flight dragstart
      // (which fires after mousemove crosses the threshold) is still suppressed.
      setTimeout(() => { resizingFlagRef.current = false }, 0)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [model, formIndex, onModelChange])

  // Frozen (kept-in-view) data columns — frozen_columns, presentation only.
  const frozen = Math.max(0, Math.min(visibleColumns.length, parseInt(String(form.frozen_columns ?? 0), 10) || 0))
  const frozenLeft = useMemo(() => {
    const out: number[] = []
    let acc = 0
    visibleColumns.forEach(({ col }) => { out.push(acc); acc += col.width || DEFAULT_COL_WIDTH })
    return out
  }, [visibleColumns])
  const stickyOf = (visIdx: number): React.CSSProperties => (visIdx < frozen
    ? { position: 'sticky', left: frozenLeft[visIdx], zIndex: 2, boxShadow: visIdx === frozen - 1 ? '2px 0 0 var(--color-border)' : undefined }
    : {})
  const setFrozen = (n: number) => {
    const m = cloneModel(model)
    const fm = m.sql_models[formIndex].sql_model
    if (n > 0) fm.frozen_columns = n
    else delete fm.frozen_columns
    onModelChange(m)
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'stretch', borderBottom: '1px solid var(--color-border)' }}>
      {/* Grid table — sized to natural column widths so horizontal scroll engages */}
      <div style={{
        flex: 1,
        minWidth: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        background: 'var(--color-bg)',
      }}>
        <table
          ref={tableRef}
          style={{
            borderCollapse: 'collapse',
            fontSize: '0.9625rem',
            tableLayout: 'fixed',
            // Explicit width = sum of colgroup widths. Required for
            // tableLayout:'fixed' to actually engage — with width:'auto'
            // browsers fall back to auto layout and ignore narrower `<col>`
            // widths whenever the column's content is wider, so resize
            // silently no-ops. Do NOT use 100%: that triggers proportional
            // stretching and clobbers the dragged width.
            width: `${tableWidth}px`,
          }}
        >
          <colgroup>
            {visibleColumns.map(({ col }, vi) => (
              <col key={vi} style={{ width: `${col.width || DEFAULT_COL_WIDTH}px` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {visibleColumns.map(({ col, index }, visIdx) => {
                const w = col.width || DEFAULT_COL_WIDTH
                const indicatorLeft = dropIndicator?.visIdx === visIdx && dropIndicator.side === 'left'
                const indicatorRight = dropIndicator?.visIdx === visIdx && dropIndicator.side === 'right'
                return (
                  <th
                    key={col.name}
                    id={`txn-pv-col-${formIndex}-${index}`}
                    draggable
                    onDragStart={(e) => handleHeaderDragStart(e, index)}
                    onDragEnd={handleHeaderDragEnd}
                    onDragOver={(e) => handleHeaderDragOver(e, visIdx)}
                    onDrop={(e) => handleHeaderDrop(e, visIdx)}
                    onClick={() => onSelectNode({ type: 'column', formIndex, columnIndex: index, label: col.name })}
                    style={{
                      padding: '10px 12px',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.8625rem',
                      // Global `table th` forces uppercase + extra letter-spacing.
                      // Author-supplied headings must render in their authored
                      // case (e.g. "Priority Code", "ID"), so opt out here.
                      textTransform: 'none',
                      letterSpacing: 'normal',
                      color: isColumnSelected(index) ? 'var(--color-accent)' : 'var(--color-text)',
                      background: isColumnSelected(index) ? 'var(--color-accent-weak)' : 'var(--color-surface-2)',
                      borderBottom: '2px solid var(--color-border)',
                      // Indicator via inset shadow so columns don't nudge sideways
                      // as the cursor crosses edges during drag.
                      boxShadow: indicatorLeft
                        ? 'inset 3px 0 0 var(--color-accent)'
                        : indicatorRight
                          ? 'inset -3px 0 0 var(--color-accent)'
                          : 'none',
                      cursor: 'grab',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      position: 'relative',
                      userSelect: 'none',
                      width: `${w}px`,
                      ...stickyOf(visIdx),
                    }}
                  >
                    <span style={{
                      opacity: isHidden(col) ? 0.55 : 1,
                      borderBottom: isHidden(col)
                        ? '1px dashed var(--color-text-secondary)'
                        : 'none',
                      paddingBottom: isHidden(col) ? 1 : 0,
                    }}>
                      {formatColumnLabel(col)}
                    </span>
                    {isMandatory(col) && <span style={{ color: 'var(--color-error, #cf222e)', marginLeft: 2 }}>*</span>}
                    {col.key && <span style={{ color: 'var(--color-warning, #9a6700)', marginLeft: 4, fontSize: '0.8125rem' }}>PK</span>}
                    {isHidden(col) && (
                      <span
                        title="Hidden at runtime — visible here in the designer only"
                        style={{
                          marginLeft: 6, padding: '0 5px', borderRadius: 8,
                          background: 'rgba(127,127,127,0.18)',
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.8125rem', fontWeight: 700, letterSpacing: 0.4,
                          textTransform: 'uppercase',
                        }}
                      >Hidden</span>
                    )}
                    {/* Resize handle — own dragstart preventDefault stops the
                        parent th from kicking off a column drag if the user
                        drags from the handle itself. */}
                    <div
                      draggable={false}
                      onDragStart={(e) => { e.preventDefault(); e.stopPropagation() }}
                      onMouseDown={(e) => handleResizeStart(e, index, visIdx)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute', right: 0, top: 0, bottom: 0, width: 6,
                        cursor: 'col-resize', background: 'transparent',
                      }}
                    />
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2].map(row => (
              <tr key={row} style={{ borderBottom: '1px solid var(--color-border)' }}>
                {visibleColumns.map(({ col, index }, visIdx) => (
                  <td
                    key={col.name}
                    onClick={() => onSelectNode({ type: 'column', formIndex, columnIndex: index, label: col.name })}
                    style={{
                      ...stickyOf(visIdx),
                      padding: '8px 12px',
                      color: 'var(--color-text-secondary)',
                      fontSize: '0.9375rem',
                      background: 'var(--color-bg)',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {renderGridCell(col, row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {visibleColumns.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: '0.9625rem' }}>
            No columns yet
          </div>
        )}
      </div>
      {onAddColumn && (
        <button type="button" onClick={() => onAddColumn()} title="Add a column to the grid" style={{
          flexShrink: 0, font: 'inherit', fontSize: '0.9375rem', fontWeight: 500, color: 'var(--color-accent)',
          background: 'var(--color-accent-weak)', border: 'none', borderLeft: '1.5px dashed var(--color-accent)',
          padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 6, whiteSpace: 'nowrap', cursor: 'pointer',
        }}>
          <Plus size={13} style={{ marginTop: 3 }} /> Add column
        </button>
      )}
      </div>
      <div style={{ padding: '10px 20px', fontSize: '0.875rem', color: 'var(--color-text-secondary)', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ flex: '1 1 240px' }}>Drag a heading to reorder · drag its right edge to resize · click it to edit the column</span>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} title="Data columns kept in view while the grid scrolls sideways (the record list for the header, the line grid for a line form)">
          <Snowflake size={13} /> Keep in view
          <select value={frozen} onChange={e => setFrozen(Number(e.target.value))}
            style={{ padding: '2px 6px', border: '1px solid var(--color-border)', borderRadius: 4, background: 'var(--color-input-bg)', color: 'var(--color-text)' }}>
            {Array.from({ length: Math.min(visibleColumns.length, 6) + 1 }, (_, n) => (
              <option key={n} value={n}>{n === 0 ? 'no columns' : `first ${n}`}</option>
            ))}
          </select>
        </label>
      </div>
    </div>
  )
}

function renderGridCell(col: ColumnDef, _row: number): React.ReactNode {
  if (isProtected(col)) {
    return <span style={{ color: 'var(--color-text-secondary)' }}>[{col.heading || col.name}]</span>
  }

  if (col.lookup || (col.edit_mask?.mask === 'ddlb')) {
    return (
      <select style={{
        width: '100%', padding: '2px 4px', fontSize: '0.9375rem',
        background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
        borderRadius: 3, color: 'var(--color-text)',
      }}>
        <option value="">Select...</option>
        {Array.isArray(col.edit_mask?.values) && col.edit_mask.values.map((v, i) => (
          <option key={i} value={v.data}>{v.display}</option>
        ))}
      </select>
    )
  }

  if (col.edit_mask?.mask === 'bool_check') {
    return <input type="checkbox" />
  }

  if (col.edit_mask?.mask === 'radio') {
    return (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {(col.edit_mask.values || []).map((v, i) => (
          <label key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.9125rem', color: 'var(--color-text)' }}>
            <input type="radio" name={`grid-${col.name}`} />
            {v.display || v.data}
          </label>
        ))}
      </div>
    )
  }

  // Presentation widgets in the grid mock: a compact chip for file uploads,
  // a hint for URLs. Textarea falls through to a single-line text cell.
  if (hasImageUpload(col) || hasDocumentUpload(col)) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4, padding: '2px 6px', fontSize: '0.8125rem',
        color: 'var(--color-text-secondary)', border: '1px dashed var(--color-border)',
        borderRadius: 3, background: 'var(--color-bg-secondary)',
      }}>
        {hasImageUpload(col) ? '🖼' : '📄'}
      </div>
    )
  }

  const inputType = (() => {
    if (hasUrlWidget(col)) return 'text'
    switch ((col.col_type || '').toUpperCase()) {
      case 'NUMBER': return 'number'
      case 'DATE': return 'date'
      case 'DATETIME': return 'datetime-local'
      default: return 'text'
    }
  })()

  let value = ''
  if (col.default_value) {
    const calVal = calendarDefaultForInput(col.default_value, inputType)
    if (calVal !== null) {
      value = calVal
    } else if (col.default_value.toLowerCase() === 'now()') {
      value = inputType === 'date' ? new Date().toISOString().slice(0, 10)
        : inputType === 'datetime-local' ? new Date().toISOString().slice(0, 16)
        : ''
    } else {
      value = col.default_value
    }
  }

  return (
    <input type={inputType}
      defaultValue={value}
      placeholder={hasUrlWidget(col) ? 'https://…' : (col.descr || '')}
      style={{
        width: '100%', padding: '2px 4px', fontSize: '0.9375rem',
        background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
        borderRadius: 3, color: 'var(--color-text)',
      }}
    />
  )
}
